import React, { useEffect, useState, useRef } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonSpinner,
  IonIcon,
  IonButton,
  useIonViewWillLeave,
} from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { checkmarkCircleOutline, closeCircleOutline } from 'ionicons/icons';
import { useLocation, useParams } from 'react-router-dom';
import { postIdvConfirmVerification } from './idv';
import { CLAIMS_LEVEL } from './types';
import { PhoneLogic } from '@/modules/phone';
import { StorageService } from '@/services/storage';
import PageRefresher from '@/components/PageRefresher';
import {
  getCrossTabCommunication,
  hasOtherTabs,
  cleanupTabId,
} from '@/services/crossTabCommunication';
import { useUserContext } from '@/modules/user/useUserContext';
import { pxToRem } from '@/modules/config/constants';

const IDVConfirmation: React.FC = () => {
  const router = useIonRouter();
  const location = useLocation();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });
  const params = useParams<{ id: string }>();
  const { refreshUserContext, setPhone, setEmail } = useUserContext();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [isMultiTabVerification, setIsMultiTabVerification] = useState(false);
  const continuePathRef = useRef<string>('/');
  const hasProcessedRef = useRef(false);
  const willStayOnPageRef = useRef(false);

  const redirectToContinuePath = () => {
    router.push(continuePathRef.current, 'forward', 'replace');
  };

  useEffect(() => {
    void hasOtherTabs();
  }, []);

  useEffect(() => {
    return () => {
      void cleanupTabId();
    };
  }, []);

  useEffect(() => {
    if (hasProcessedRef.current) {
      return;
    }

    const handleVerification = async () => {
      const id = params.id;
      if (!id) {
        setStatus('error');
        setErrorMessage('No verification ID provided');
        hasProcessedRef.current = true;
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setErrorMessage('No verification token provided');
        hasProcessedRef.current = true;
        return;
      }

      hasProcessedRef.current = true;

      try {
        const result = await postIdvConfirmVerification(id, token);

        await refreshUserContext().catch(error => {
          // eslint-disable-next-line no-console
          console.error('Failed to refresh user context after IDV:', error);
        });

        // The wizard stashes a not-yet-verified phone change under
        // PENDING_PHONE so a reload mid-verification restores the prior
        // number. Following the SMS magic link confirms here instead of in the
        // wizard's modal, so its in-app success handler never runs — commit the
        // pending number now that this phone verification has succeeded.
        // Gated to phone-level confirmations so an email link can't promote a
        // stale stash.
        if (result.verification?.level === CLAIMS_LEVEL.PHONE) {
          const pending = await StorageService.getPendingPhone();
          if (pending) {
            const promoted = PhoneLogic.tryCreate(pending);
            if (promoted) {
              setPhone(promoted);
            } else {
              await StorageService.clearPendingPhone().catch(() => {});
            }
          }
        }

        // The wizard likewise stashes a not-yet-verified email change under
        // PENDING_EMAIL. Following the email magic link confirms here instead
        // of in the wizard's modal, so its in-app success handler never runs —
        // commit the pending address now that this email verification has
        // succeeded. Gated to email-level confirmations so a phone link can't
        // promote a stale stash.
        if (result.verification?.level === CLAIMS_LEVEL.EMAIL) {
          const pending = await StorageService.getPendingEmail();
          if (pending) {
            setEmail(pending);
          }
        }

        const path = result.verification?.continue || '/';
        continuePathRef.current = path;
        const crossTab = getCrossTabCommunication();
        crossTab.broadcastIDVCompletion(path);

        let checkCount = 0;
        const maxChecks = 5;
        const checkInterval = 200;
        let redirectTimeout: ReturnType<typeof setTimeout> | null = null;

        const checkForOtherTabs = async () => {
          const hasOtherTabsOpen = await hasOtherTabs();

          if (hasOtherTabsOpen) {
            willStayOnPageRef.current = true;
            if (redirectTimeout) {
              clearTimeout(redirectTimeout);
              redirectTimeout = null;
            }
            setIsMultiTabVerification(true);
            setStatus('success');
          } else if (checkCount >= maxChecks) {
            setIsMultiTabVerification(false);
            setStatus('success');
            redirectTimeout = setTimeout(() => redirectToContinuePath(), 1500);
          } else {
            checkCount++;
            setTimeout(checkForOtherTabs, checkInterval);
          }
        };

        const fallbackTimeout = setTimeout(() => {
          if (!willStayOnPageRef.current) {
            setIsMultiTabVerification(false);
            setStatus('success');
            redirectToContinuePath();
          }
        }, 2000);

        setTimeout(() => {
          checkForOtherTabs();
          setTimeout(
            () => {
              clearTimeout(fallbackTimeout);
            },
            maxChecks * checkInterval + 500
          );
        }, 100);
      } catch (error) {
        setStatus('error');
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to verify identity'
        );
      }
    };

    handleVerification();
    // Depend on params.id and location.search - refreshUserContext is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, location.search]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Identity Verification</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <PageRefresher />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            padding: '20px',
          }}
        >
          {status === 'loading' && (
            <IonCard>
              <IonCardContent style={{ textAlign: 'center', padding: '40px' }}>
                <IonSpinner name="crescent" style={{ marginBottom: '20px' }} />
                <p>Verifying your identity...</p>
              </IonCardContent>
            </IonCard>
          )}

          {status === 'success' && (
            <IonCard color="success">
              <IonCardContent style={{ textAlign: 'center', padding: '40px' }}>
                <IonIcon
                  icon={checkmarkCircleOutline}
                  style={{ fontSize: pxToRem(64), marginBottom: '20px' }}
                />
                <h2>Verification Successful!</h2>
                {isMultiTabVerification ? (
                  <>
                    <p style={{ fontSize: pxToRem(16), marginTop: '20px' }}>
                      This may be a duplicate window that you can now close and
                      return to the app where you started. Or press Continue to
                      keep keeping on here.
                    </p>
                    <div style={{ marginTop: '20px' }}>
                      <IonButton
                        expand="block"
                        onClick={() => redirectToContinuePath()}
                        color="primary"
                      >
                        Continue
                      </IonButton>
                    </div>
                  </>
                ) : (
                  <p>Redirecting...</p>
                )}
              </IonCardContent>
            </IonCard>
          )}

          {status === 'error' && (
            <IonCard color="danger">
              <IonCardContent style={{ textAlign: 'center', padding: '40px' }}>
                <IonIcon
                  icon={closeCircleOutline}
                  style={{ fontSize: pxToRem(64), marginBottom: '20px' }}
                />
                <h2>Verification Failed</h2>
                <p>{errorMessage}</p>
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={() => router.push('/topics', 'forward', 'replace')}
                    style={{
                      padding: '10px 20px',
                      fontSize: pxToRem(16),
                      backgroundColor: 'var(--ion-color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Go to topics
                  </button>
                </div>
              </IonCardContent>
            </IonCard>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default IDVConfirmation;
